import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { forkJoin, from, Subject } from "rxjs";
import { filter, finalize, switchMap, take, takeUntil } from "rxjs/operators";

import { ScoreLocalService } from "@app/pages/score/score.service";
import { BlockingUiSignalService } from "../blocking-ui-signal.service";
import { FeatureFlagService } from "../feature-flag.service";
import { FeatureFlagKeys } from "../feature-flag.models";
import { InteractionTrackingService, InteractionType } from "../interaction-tracking.service";
import { OnboardingStepService } from "../onboarding-step.service";
import { PaymentsService } from "../payments.service";
import {
  isOnHome,
  OnboardingModalExit,
  openOnboardingCentredModal,
  whenUiQuiet,
} from "./onboarding-lesson-modal";

export const H01_LESSON_ID = "H01";

/** Recorded with H01's completed lesson state (spec §26.11) - see the class comment. */
export const HOME_INTRODUCTION_VIEWED = "home_introduction_viewed";

/** Where "Start Score" goes - the new scoring session itself, not the Score landing page. */
export const SCORE_SESSION_ROUTE = "/score/stars-and-shadows";

/** Copy rows H01.01-H01.04, workbook v2.37 Copy sheet (all Reviewed: Confirmed). */
export const H01_COPY = {
  heading: "Start with a Score",
  body:
    "A Score helps you check in with what is supporting you and what is placing demand on you " +
    "right now. It gives you a picture of your current Safety Buffer and a useful starting point " +
    "for your day.",
  primaryLabel: "Start Score",
  secondaryLabel: "Explore first",
};

/**
 * Onboarding lesson H01 "Start with a Score" (FLIP-334; spec v2.35 §8.1, workbook v2.37).
 * Informational centred modal over Home.
 *
 * - Eligible on a Home arrival while the `onboarding-walkthrough` flag is on, H01 has not been
 *   completed or dismissed, and the user has never submitted a Score. That last check is also
 *   the "suppress if the first Score was completed independently" rule and the existing-user
 *   rule (established Score history suppresses H01), so no backfill is needed.
 * - Opens only once no blocking product modal/snackbar is open, or about to be
 *   (BlockingUiSignalService), and the user is still on Home.
 * - Every exit completes H01 permanently: Start Score, Explore first, or the standard
 *   informational dismissal (close X / Escape / Android Back). Backdrop taps do nothing.
 * - The milestone `home_introduction_viewed` is defined as written at the same moment as H01's
 *   completed state, so H01's CompletedAt on its UserOnboardingStep row *is* that milestone -
 *   no separate milestone store exists yet. The key is named in the tracking detail.
 * - Start Score opens a new scoring session directly and marks the entry so S01 suppresses
 *   itself for that session (FLIP-339).
 * - H02A's Home card (FLIP-335) holds back until H01 has completed, so the card never sits behind
 *   this modal; `completed$` lets it appear as soon as H01 closes on Home (not on Start Score,
 *   which is already leaving Home).
 */
@Injectable({ providedIn: "root" })
export class H01HomeIntroductionService {
  /**
   * Emits when H01 completes and the user stays on Home (every exit except Start Score), so H02A's
   * Home card can take over (FLIP-335). Start Score is leaving Home, so it doesn't emit.
   */
  readonly completed$ = new Subject<void>();

  private readonly cancel$ = new Subject<void>();
  private inFlight = false;
  /** Set once H01 has opened in this app session, so a re-arrival on Home doesn't re-check it. */
  private shown = false;
  /** Set once H01 completed in this app session - its row write may still be in flight. */
  private completedInSession = false;

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private blockingUiSignal: BlockingUiSignalService,
    private featureFlagService: FeatureFlagService,
    private paymentsService: PaymentsService,
    private onboardingStepService: OnboardingStepService,
    private scoreLocalService: ScoreLocalService,
    private interactionTracking: InteractionTrackingService
  ) {
    // Logout emits false - forget this session's state so a different account signing in on the
    // same tab gets its own check.
    this.paymentsService.subscriptionActive$.pipe(filter((active) => !active)).subscribe(() => {
      this.shown = false;
      this.completedInSession = false;
      this.cancel();
    });
  }

  /**
   * Whether H01 completed in this app session (reset on logout). H02A reads this alongside H01's
   * row so its Home card appears as soon as H01 closes, even before the completed write lands.
   */
  get completedThisSession(): boolean {
    return this.completedInSession;
  }

  /** Call on each Home arrival. No-op if a check is already running or H01 has already shown. */
  maybeShow(): void {
    if (this.inFlight || this.shown) {
      return;
    }
    this.inFlight = true;

    // Wait for a confirmed subscription first: that's when app.component's authenticated boot
    // fan-out runs, which starts the flag fetch and registers the boot-modal pending checks.
    this.paymentsService.subscriptionActive$
      .pipe(
        filter((active) => active),
        take(1),
        switchMap(() => from(this.featureFlagService.init())),
        filter(() => this.featureFlagService.isEnabled(FeatureFlagKeys.OnboardingWalkthrough)),
        switchMap(() =>
          forkJoin([
            this.onboardingStepService.getStep(H01_LESSON_ID),
            this.onboardingStepService.hasSubmittedScore(),
          ])
        ),
        // Only a definite "no row / not finished" and a definite "no Score" qualify - undefined or
        // null means the check failed, and a lesson must not guess its way into showing.
        filter(
          ([step, hasScore]) =>
            step !== undefined && !step?.completedAt && !step?.dismissed && hasScore === false
        ),
        switchMap(() => whenUiQuiet(this.blockingUiSignal.isBlockingUiActive$)),
        filter(() => isOnHome(this.router)),
        takeUntil(this.cancel$),
        finalize(() => (this.inFlight = false))
      )
      .subscribe(() => this.open());
  }

  /** Call when leaving Home - abandons a pending check so H01 never opens over another page. */
  cancel(): void {
    this.cancel$.next();
  }

  private open(): void {
    this.shown = true;
    const now = new Date();
    this.onboardingStepService.upsertStep({ stepKey: H01_LESSON_ID, startedAt: now, lastViewedAt: now });
    this.track("onboarding_lesson_state_changed", "started");

    const exit$ = openOnboardingCentredModal(this.dialog, H01_COPY);
    this.track("onboarding_lesson_presented");
    exit$.subscribe((exit) => this.complete(exit));
  }

  /** Informational: every exit - either button or the standard dismissal - completes H01. */
  private complete(exit: OnboardingModalExit): void {
    this.completedInSession = true;
    this.onboardingStepService.upsertStep({ stepKey: H01_LESSON_ID, completedAt: new Date() });

    const action = exit === "primary" ? "start_score" : exit === "secondary" ? "explore_first" : "dismiss";
    this.track("onboarding_lesson_action", `action: ${action}`);
    this.track("onboarding_lesson_state_changed", `completed; milestone: ${HOME_INTRODUCTION_VIEWED}`);

    if (exit === "primary") {
      // Marked before navigating: S01 (FLIP-339) suppresses itself for a session H01 started.
      this.scoreLocalService.markOnboardingDirectEntry();
      this.router.navigate([SCORE_SESSION_ROUTE]);
      // Not emitting completed$: navigation is async, so H02A would still see Home and flash its
      // card before the Score screen. It shows on the next Home arrival instead.
      return;
    }
    this.completed$.next();
  }

  /** Same shape as CoachMarkService's FLIP-322 lesson events, so both surfaces report alike. */
  private track(eventName: string, detail?: string): void {
    this.interactionTracking.track({
      screen: this.router.url,
      eventName,
      interactionType: InteractionType.Selection,
      entityType: "OnboardingLesson",
      detail: detail ? `lesson: ${H01_LESSON_ID}; ${detail}` : `lesson: ${H01_LESSON_ID}`,
    });
  }
}
